import { PddPayloadTypes } from "@/constants";
import { Context } from "@/context";
import { postNewSettings } from "@/helpers/postNewSettings";
import { useUserDiceDataStore } from "@/hooks/useUserDiceDataStore";
import type { DiceVisibility } from "@/types";
import Volume from "@dndbeyond/fontawesome-cache/svgs/regular/volume.svg";
import XMark from "@dndbeyond/fontawesome-cache/svgs/solid/xmark.svg";
import { Button } from "@dndbeyond/ttui/components/Button";
import { Dialog } from "@dndbeyond/ttui/components/Dialog";
import clsx from "clsx";
import {
  useContext,
  type Dispatch,
  type FC,
  type MouseEvent,
  type SetStateAction,
} from "react";
import styles from "./styles.module.css";

interface SettingsModalProps {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
}

const shadowQualities = {
  1: "Low",
  2: "Medium",
  3: "High",
};
const diceVisibilityOptions = {
  shared: {
    label: "Shared Dice",
    description: "See other people's rolls",
  },
  self: {
    label: "Only My Dice",
    description: "Only see your own rolls",
  },
  disabled: {
    label: "Disable 3D Dice",
    description: "Only see notifications with roll results",
  },
};
const boolOptions = {
  true: "Enabled",
  false: "Disabled",
};

export const SettingsModal: FC<SettingsModalProps> = ({
  isOpen,
  setIsOpen,
}) => {
  const { user, nodeEnv, env, renderedWorker } = useContext(Context);
  const userDiceData = useUserDiceDataStore((state) =>
    state.getUserDiceData(Number(user?.id)),
  );
  const { setUserDiceData } = useUserDiceDataStore();

  return (
    <Dialog
      open={isOpen}
      onClose={() => setIsOpen(false)}
      modal
      className={styles.modal}
    >
      <header className={styles.modalHeader}>
        <h1 className={styles.modalHeading}>3D Dice Settings</h1>
        <Button
          variant="tool"
          size="x-small"
          className={styles.closeButton}
          onClick={() => setIsOpen(false)}
          aria-label={"Close Settings"}
        >
          <XMark />
        </Button>
      </header>
      <section className={styles.modalSection}>
        <h2 className={styles.modalH2}>Visibility</h2>
        <ul className={styles.ul}>
          {Object.entries(diceVisibilityOptions).map(
            ([key, { label, description }]) => (
              <li key={key} className={styles.li}>
                <label
                  htmlFor={`dice-visibility-option-${key}`}
                  className={styles.radioLabel}
                >
                  <p className={styles.optionText}>{label}</p>
                  <p className={styles.optionDescription}>{description}</p>
                </label>
                <input
                  type="radio"
                  id={`dice-visibility-option-${key}`}
                  name="dice-visibility-option"
                  checked={String(userDiceData?.settings.visibility) === key}
                  className={styles.radio}
                  onChange={async () => {
                    if (
                      !userDiceData ||
                      userDiceData.settings.visibility === key
                    )
                      return;

                    const newDiceData = {
                      ...userDiceData,
                      settings: {
                        ...userDiceData.settings,
                        visibility: key as DiceVisibility,
                      },
                    };

                    setUserDiceData(Number(user?.id), newDiceData);
                    await postNewSettings(newDiceData.settings, nodeEnv!, env!);
                  }}
                />
              </li>
            ),
          )}
        </ul>
        <h2 className={styles.modalH2}>Sound Effects Volume</h2>
        <div className={styles.sliderWrapper}>
          <Volume className={styles.volume} />
          <input
            type="range"
            min="0"
            max="1"
            defaultValue={userDiceData?.settings.volume}
            className={styles.slider}
            step={0.1}
            onMouseUp={async (e: MouseEvent<HTMLInputElement>) => {
              if (!userDiceData) return;

              const value = (e.target as HTMLInputElement).value;

              if (String(userDiceData.settings.volume) === value) return;

              const newDiceData = {
                ...userDiceData,
                settings: {
                  ...userDiceData?.settings,
                  volume: Number(value),
                },
              };

              renderedWorker?.postMessage({
                type: PddPayloadTypes.UserSettings,
                payload: newDiceData.settings,
              });
              setUserDiceData(Number(user?.id), newDiceData);
              await postNewSettings(newDiceData.settings, nodeEnv!, env!);
            }}
          />
        </div>
        <h2 className={styles.modalH2}>Particle Effects</h2>
        <div className={styles.particleEffectsContainer}>
          <ul className={clsx([styles.ul, styles.horizontalUl])}>
            {Object.entries(boolOptions).map(([key, label]) => (
              <li key={key} className={styles.li}>
                <label
                  htmlFor={`particle-effects-option-${key}`}
                  className={styles.radioLabel}
                >
                  {label}
                </label>
                <input
                  type="radio"
                  id={`particle-effects-option-${key}`}
                  name="particle-effects-option"
                  checked={
                    String(userDiceData?.settings.particlesEnabled) === key
                  }
                  className={styles.radio}
                  onChange={async () => {
                    if (!userDiceData) return;

                    const newSettings = {
                      ...userDiceData.settings,
                      particlesEnabled: !userDiceData.settings.particlesEnabled,
                    };

                    renderedWorker?.postMessage({
                      type: PddPayloadTypes.UserSettings,
                      payload: newSettings,
                    });
                    setUserDiceData(Number(user?.id), {
                      ...userDiceData,
                      settings: newSettings,
                    });
                    await postNewSettings(newSettings, nodeEnv!, env!);
                  }}
                />
              </li>
            ))}
          </ul>
        </div>
        <h2 className={styles.modalH2}>Post-Processing Effects</h2>
        <p className={styles.p}>
          Disable this if your browser is having performance issues
        </p>
        <div className={styles.postProcessingEffectsContainer}>
          <ul className={clsx([styles.ul, styles.horizontalUl])}>
            {Object.entries(boolOptions).map(([key, label]) => (
              <li key={key} className={styles.li}>
                <label
                  htmlFor={`post-processing-effects-option-${key}`}
                  className={styles.radioLabel}
                >
                  {label}
                </label>
                <input
                  type="radio"
                  id={`post-processing-effects-option-${key}`}
                  name="post-processing-effects-option"
                  checked={
                    String(userDiceData?.settings.isPostProcessingEnabled) ===
                    key
                  }
                  className={styles.radio}
                  onChange={async () => {
                    if (!userDiceData) return;

                    const newSettings = {
                      ...userDiceData.settings,
                      isPostProcessingEnabled:
                        !userDiceData.settings.isPostProcessingEnabled,
                    };

                    renderedWorker?.postMessage({
                      type: PddPayloadTypes.UserSettings,
                      payload: newSettings,
                    });
                    setUserDiceData(Number(user?.id), {
                      ...userDiceData,
                      settings: newSettings,
                    });
                    await postNewSettings(newSettings, nodeEnv!, env!);
                  }}
                />
              </li>
            ))}
          </ul>
        </div>
        <h2 className={styles.modalH2}>Shadow Quality</h2>
        <ul className={clsx([styles.ul, styles.horizontalUl])}>
          {Object.entries(shadowQualities)
            .reverse()
            .map(([key, value]) => (
              <li key={key} className={styles.li}>
                <label
                  htmlFor={`shadow-quality-${key}`}
                  className={styles.radioLabel}
                >
                  {value}
                </label>
                <input
                  type="radio"
                  id={`shadow-quality-${key}`}
                  name="shadow-quality"
                  checked={String(userDiceData?.settings.shadowQuality) === key}
                  className={styles.radio}
                  onChange={async () => {
                    if (
                      !userDiceData ||
                      String(userDiceData.settings.shadowQuality) === key
                    )
                      return;

                    const newDiceData = {
                      ...userDiceData,
                      settings: {
                        ...userDiceData.settings,
                        shadowQuality: Number(key),
                      },
                    };

                    renderedWorker?.postMessage({
                      type: PddPayloadTypes.UserSettings,
                      payload: newDiceData.settings,
                    });
                    setUserDiceData(Number(user?.id), newDiceData);
                    await postNewSettings(newDiceData.settings, nodeEnv!, env!);
                  }}
                />
              </li>
            ))}
        </ul>
      </section>
    </Dialog>
  );
};
